/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */

import { STORE_PROGRAM_ID_HEADER } from '@src/constants/api.headers.constants'
import { getSvdExperimentValue } from '@src/experiments/statsig-integration/get-svd-experiment-value'
import { getStoreProgramIdFromStorage } from '@src/utils/b2b'

import Product from '../../domains/Product'
import QuinceApiError from '../../domains/quince-api-error'
import AuthUtil from '../../utils/authUtil'
import { isHttpClientError, storefrontCheckoutServicesHttpClient } from '../../utils/HttpClient'
import { normalizeId } from '../cartService/cart.service.b2b.utils'
import { captureCartRegionSnapshot, reportCartRegionProvenance } from '../cartService/report-cart-region-provenance'
import { appendEDDExpBucketToHeaders } from '../eddService/eddService.helper'
import { fetchAndEnrichProductsV2 } from '../productService/fetch-and-enrich-products-v2'
import { fetchAndEnrichProducts } from '../productService/fetchAndEnrichProducts'

import { getCheckoutId, formatToYYYYMMDD } from './cartCheckoutService.helper'
import { getDataWithProductAndInventory, setCartAuthCodeFromHeader } from './checkout.service.helper'
import { Cart, GiftingDetails, LineItems, TBuyNowApiResponse } from './checkout.service.types'

import type { CustomizationEditPayload } from '../cartService/cart.service.request.types'

const HTTP_NOT_FOUND = 404

export const getCheckoutWithId = async (checkoutId: string): Promise<any> => {
  try {
    const res = await storefrontCheckoutServicesHttpClient.get(`/checkouts/${checkoutId}`)
    setCartAuthCodeFromHeader(res)
    const { data } = res
    return await getDataWithProductAndInventory(data)
  } catch (e) {
    console.error('Failed while fetching getCheckoutWithId(), API - `/checkouts/${checkoutId}`', e)
    throw e
  }
}

export const getProductData = async ({ productIds }: { productIds: (number | string)[] }): Promise<Product[]> => {
  // Smart Variant / VDA v2 — this service is the EDD-experiment twin of
  // cartService and also feeds the cart screen; same cutover rules apply.
  // This path historically fetched FULL enrichment (no useLite), so the v2
  // call mirrors that with useLite: false. B2B self-serve stays on v1.
  const useV2Enrich = getSvdExperimentValue() && !getStoreProgramIdFromStorage()
  if (useV2Enrich) {
    return await fetchAndEnrichProductsV2({
      productIds,
      useLite: false,
    })
  }

  const products = await fetchAndEnrichProducts({
    internalProductIds: productIds,
  })
  return products
}

export const getIsCartActive = async (): Promise<any> => {
  try {
    const userSession = await AuthUtil.getUserSession()
    if (!userSession?.idToken?.jwtToken) return
    const res = await storefrontCheckoutServicesHttpClient.get(`/checkouts/active`, {
      headers: {
        Authorization: `Bearer ${userSession?.idToken?.jwtToken}`,
      },
    })
    setCartAuthCodeFromHeader(res)
    const { data } = res
    return await getDataWithProductAndInventory(data)
  } catch (e) {
    console.error('Failed while fetching getIsCartActive(), API - `/carts/active`', e)
    // throw e
  }
}

export const mergeCustomerCart = async (previousCartId: string, storeProgramId?: string | null): Promise<any> => {
  try {
    const userSession = await AuthUtil.getUserSession()
    const headers: Record<string, string> = {}
    if (userSession?.idToken?.jwtToken) {
      headers.Authorization = `Bearer ${userSession.idToken.jwtToken}` // eslint-disable-line i18next/no-literal-string
    }
    if (storeProgramId) {
      headers[STORE_PROGRAM_ID_HEADER] = storeProgramId
    }
    const { data } = await storefrontCheckoutServicesHttpClient.post(
      `/checkouts/${previousCartId}/merge-customer-checkout`,
      {},
      { headers },
    )
    return await getDataWithProductAndInventory(data?.second)
  } catch (e) {
    console.error('Failed while fetching mergeCustomerCart(), API - `/merge-customer-checkout`', e)
    return {}
  }
}

export const getCheckouts = async (
  lineItems: LineItems,
  previousCheckoutId?: string,
  isDOBRequired?: boolean,
  dateOfBirth?: string,
  extraParams?: Record<string, unknown>,
): Promise<Cart | undefined> => {
  try {
    const requestBody = {
      lineItems,
      ...(isDOBRequired && dateOfBirth && { customerDateOfBirth: formatToYYYYMMDD(dateOfBirth) }),
      ...(extraParams && { ...extraParams }),
    }
    // Snapshot the client region identifiers BEFORE the request so they are as-of cart birth.
    const regionSnapshot = captureCartRegionSnapshot()
    const res = await storefrontCheckoutServicesHttpClient.post(
      previousCheckoutId
        ? `/checkouts/create-for-line-items?previousCheckoutId=${previousCheckoutId}`
        : `/checkouts/create-for-line-items`,
      requestBody,
    )
    setCartAuthCodeFromHeader(res)
    const { data } = res

    // eslint-disable-next-line i18next/no-literal-string
    reportCartRegionProvenance(res, { createSource: 'checkouts', snapshot: regionSnapshot })
    return await getDataWithProductAndInventory(data)
  } catch (e) {
    console.error('Failed while fetching getCheckouts(), API - `/checkouts/create-for-line-items`', e)
  }
  return
}

export const getPreviousCheckouts = async (previousCheckoutId: string): Promise<Cart | undefined> => {
  try {
    const regionSnapshot = captureCartRegionSnapshot()
    const res = await storefrontCheckoutServicesHttpClient.post(
      `/checkouts/create-checkout-if-possible/${previousCheckoutId}`,
      {},
    )
    setCartAuthCodeFromHeader(res)
    const { data } = res
    if (data.createdCheckout) {
      // eslint-disable-next-line i18next/no-literal-string
      reportCartRegionProvenance(res, { createSource: 'previous-checkout', snapshot: regionSnapshot })
      return await getDataWithProductAndInventory(data.checkout)
    }
  } catch (e) {
    console.error(
      'Failed while fetching getPreviousCheckouts(), API - `/checkouts/create-checkout-if-possible/${previousCheckoutId}`',
      e,
    )
  }
}

export const updateItemQuantity = async (
  checkoutId: string,
  lineItems: {
    lineItemId: string | null | undefined
    adjustment: number
  }[],
): Promise<any> => {
  try {
    const { data } = await storefrontCheckoutServicesHttpClient.put(
      `/checkouts/${checkoutId}/cart/update-items-quantity`,
      {
        lineItems,
      },
    )
    return await getDataWithProductAndInventory(data)
  } catch (e) {
    console.error(
      'Failed while fetching updateItemQuantity(), API - `/checkouts/${checkoutId}/cart/update-item-quantity`',
      e,
    )
  }
  return ''
}

export const removeItem = async (_cartId: string, lineItemId: string): Promise<string> => {
  const checkoutId = getCheckoutId()
  try {
    const { data } = await storefrontCheckoutServicesHttpClient.put(`/checkouts/${checkoutId}/cart/remove-item`, {
      lineItemId,
    })
    return await getDataWithProductAndInventory(data)
  } catch (e) {
    console.error('Failed while fetching removeItemQyantity(), API - `/checkouts/${checkoutId}/cart/remove-item', e)
  }
  return ''
}

export const removeDiscountCode = async (_cartId: string): Promise<string> => {
  const checkoutId = getCheckoutId()
  try {
    const { data } = await storefrontCheckoutServicesHttpClient.put(
      `/checkouts/${checkoutId}/cart/remove-discount-code`,
      {},
    )
    return await getDataWithProductAndInventory(data)
  } catch (e) {
    console.error(
      'Failed while fetching removeDiscountCode(), API - `/checkouts/${checkoutId}/cart/remove-discount-code',
      e,
    )
  }
  return ''
}

export const markCartComplete = async (_cartId: string): Promise<string> => {
  const checkoutId = getCheckoutId()
  try {
    const { data } = await storefrontCheckoutServicesHttpClient.put(
      `/checkouts/${checkoutId}/cart/mark-cart-complete`,
      {},
    )
    return await getDataWithProductAndInventory(data)
  } catch (e) {
    console.error(
      'Failed while fetching markCartComplete(), API - `/checkouts/${checkoutId}/cart/mark-cart-complete',
      e,
    )
  }
  return ''
}

export const applyDiscountCode = async (checkoutId: string, discountCode: string): Promise<string> => {
  try {
    const { data } = await storefrontCheckoutServicesHttpClient.put(
      `/checkouts/${checkoutId}/cart/apply-discount-code`,
      {
        discountCode,
      },
    )
    return await getDataWithProductAndInventory(data)
  } catch (e) {
    console.error(
      'Failed while fetching applyDiscountCode(), API - `/checkouts/${checkoutId}/cart/apply-discount-code',
      e,
    )
  }
  return ''
}

export const addItemToCart = async (lineItems: LineItems, extraParams?: Record<string, unknown>) => {
  const checkoutId = getCheckoutId()
  try {
    const { data } = await storefrontCheckoutServicesHttpClient.put(`/checkouts/${checkoutId}/cart/add-item`, {
      lineItems,
      ...(extraParams && { ...extraParams }),
    })

    return await getDataWithProductAndInventory(data)
  } catch (e) {
    console.error('Failed while fetching addItemToCart(), API - `/checkouts/${checkoutId}/cart/add-item`', e)
  }
  return ''
}

export const updateDateOfBirth = async (dob: string) => {
  const checkoutId = getCheckoutId()
  try {
    const { data } = await storefrontCheckoutServicesHttpClient.put(
      `/checkouts/${checkoutId}/cart/update-date-of-birth`,
      {
        dateOfBirth: formatToYYYYMMDD(dob),
      },
    )

    return data
  } catch (e) {
    console.error('Failed while updating date of birth, API - `/checkouts/${checkoutId}/cart/update-date-of-birth`', e)
    const quinceError = new QuinceApiError({
      error: e as Error,
      message: 'Failed on updateDateOfBirth',
      apiName: 'DOB_ERROR',
    })
    throw quinceError
  }
}

export const buyNowCartApi = async (
  lineItems: LineItems,
  parentCartAuthMapping?: {
    id: string
    authCode: string
  },
  discountCode?: string,
): Promise<TBuyNowApiResponse | undefined> => {
  try {
    const checkoutId = getCheckoutId()
    const regionSnapshot = captureCartRegionSnapshot()
    const res = await storefrontCheckoutServicesHttpClient.post(`/checkouts/${checkoutId}/cart/buy-now-cart`, {
      lineItems,
      parentCartAuthMapping,
      ...(discountCode && { discountCode }),
    })
    const { data } = res
    // The parent cart is the one persisted to localStorage, so it is the cart that can 422 later.
    reportCartRegionProvenance(res, {
      createSource: 'buy-now-checkout', // eslint-disable-line i18next/no-literal-string
      cartId: (data as TBuyNowApiResponse | undefined)?.parentCartAuthMapping?.id ?? null,
      snapshot: regionSnapshot,
    })
    return data
  } catch (e) {
    console.error('Failed while fetching buyNowCart(), API - `/carts/buy-now-cart`', e)
  }
  return
}

export const updateGiftingDetails = async (giftingDetails: GiftingDetails): Promise<any> => {
  const checkoutId = getCheckoutId()
  try {
    const res = await storefrontCheckoutServicesHttpClient.put(`/checkouts/${checkoutId}/cart/update-gifting-details`, {
      recipientEmail: giftingDetails.recipientEmail,
      recipientName: giftingDetails.recipientName,
      senderName: giftingDetails.senderName,
      giftMessage: giftingDetails.giftMessage,
    })
    setCartAuthCodeFromHeader(res)
    const { data } = res
    return await getDataWithProductAndInventory(data)
  } catch (e) {
    console.error('Failed while updating gifting API - `/update-gifting-details`', e)
    throw e
  }
}

export const updateGiftingFlag = async (isGift: boolean): Promise<any> => {
  const checkoutId = getCheckoutId()
  try {
    const res = await storefrontCheckoutServicesHttpClient.put(
      `/checkouts/${checkoutId}/cart/update-gifting-flag?isGift=${isGift}`,
      {},
    )
    setCartAuthCodeFromHeader(res)
    const { data } = res
    return await getDataWithProductAndInventory(data)
  } catch (e) {
    console.error('Failed while updating gifting API - `/update-gifting-flag`', e)
    throw e
  }
}

export const removeAllOutOfStockItemsFromCart = async (): Promise<any> => {
  const checkoutId = getCheckoutId()
  try {
    const { data } = await storefrontCheckoutServicesHttpClient.put(
      `/checkouts/${checkoutId}/cart/remove-all-out-of-stock-items`,
      {},
    )
    const cart = await getDataWithProductAndInventory(data)
    return cart
  } catch (e) {
    console.error(
      'Failed while removing all out of stock items API - `/checkouts/${checkoutId}/cart/remove-all-out-of-stock-items`',
      e,
    )
    throw e
  }
}

// save for later

export const saveItemForLater = async (
  cartId: string,
  saveForLaterLineItems: {
    lineItemId: string | null | undefined
  }[],
): Promise<any> => {
  const checkoutId = getCheckoutId()

  try {
    const { data } = await storefrontCheckoutServicesHttpClient.put(
      `/checkouts/${checkoutId}/cart/save-items-for-later`,
      {
        saveForLaterLineItems,
      },
    )
    const cart = await getDataWithProductAndInventory(data)
    return cart
  } catch (e) {
    console.error(
      'Failed while fetching saveItemForLater(), API - `/checkouts/${checkoutId}/cart/save-items-for-later`',
      e,
    )
    throw e
  }
}

export const moveItemBackToCart = async (
  cartId: string,
  moveToCartLineItems: {
    lineItemId: string | null | undefined
  }[],
): Promise<any> => {
  const checkoutId = getCheckoutId()
  try {
    const { data } = await storefrontCheckoutServicesHttpClient.put(
      `/checkouts/${checkoutId}/cart/move-items-to-cart`,
      {
        moveToCartLineItems,
      },
    )
    const cart = await getDataWithProductAndInventory(data)
    return cart
  } catch (e) {
    console.error(
      'Failed while fetching moveItemBackToCart(), API - `/checkouts/${checkoutId}/cart/move-items-to-cart`',
      e,
    )
    throw e
  }
}

// is checkout active

export const getIsCheckoutActive = async (): Promise<any> => {
  try {
    const userSession = await AuthUtil.getUserSession()
    if (!userSession?.idToken?.jwtToken) return
    const res = await storefrontCheckoutServicesHttpClient.get(`/checkouts/active`, {
      headers: {
        Authorization: `Bearer ${userSession?.idToken?.jwtToken}`,
      },
    })
    setCartAuthCodeFromHeader(res)
    const { data } = res
    return await getDataWithProductAndInventory(data)
  } catch (e) {
    console.error('Failed while fetching getIsCheckoutActive(), API - `/checkouts/active`', e)
    // throw e
  }
}

export const refreshFulfillmentDetails = async (
  zipCode: string,
  checkoutId: string | null = getCheckoutId(),
  ignoreIfNotExpired?: boolean,
): Promise<any> => {
  try {
    if (!zipCode) {
      return
    }

    if (!checkoutId) {
      console.warn('Checkout ID is not available')
      return
    }

    const eddHeaders = appendEDDExpBucketToHeaders()

    const { data } = await storefrontCheckoutServicesHttpClient.post(
      `/checkouts/${checkoutId}/refresh-cart-fulfillment-package`,
      {
        zipCode,
        ignoreIfNotExpired,
      },
      {
        headers: eddHeaders,
      },
    )

    return data
  } catch (e) {
    console.error(
      'Failed while fetching refreshFulfillmentDetails(), API - `/checkouts/${checkoutId}//checkouts/{checkoutId}/refresh-cart-fulfillment-package`',
      e,
    )
  }
}

export const updateCustomizationLineItem = async (cartId: string, lineItemId: string, customizationDetails: any) => {
  try {
    const { data } = await storefrontCheckoutServicesHttpClient.put(
      `/carts/${cartId}/customization-items/${lineItemId}`,
      {
        ...customizationDetails,
      },
    )
    return data
  } catch (e) {
    console.error(
      `Failed while calling updateCustomizationLineItem(), API - /carts/${cartId}/customization-items/${lineItemId}`,
      e,
    )
    throw e
  }
}
/**
 * Update subscription for a line item
 * - Convert one-time purchase to subscription (provide planId)
 * - Convert subscription to one-time (planId = null)
 * - Update subscription frequency (change planId)
 */
export const updateSubscription = async (
  checkoutId: string,
  lineItemId: string,
  planId: number | null,
  intent: string,
): Promise<Cart | undefined> => {
  try {
    const payload = {
      subscriptionPlanId: planId,
      subscriptionIntent: intent,
    }
    const { data } = await storefrontCheckoutServicesHttpClient.patch(
      `/checkouts/${checkoutId}/cart/update-line-item/${lineItemId}`,
      payload,
    )
    return await getDataWithProductAndInventory(data)
  } catch (e) {
    console.error(
      `Failed while updating subscription, API - /checkouts/${checkoutId}/cart/update-line-item/${lineItemId}`,
      e,
    )
    throw e
  }
}

// --- B2B helpers ---

const resolveDeleteError = async (error: unknown, checkoutId: string): Promise<Cart> => {
  if (isHttpClientError(error) && error.response?.status === HTTP_NOT_FOUND) {
    const latest = await getCheckoutWithId(checkoutId)
    if (!latest?.id) throw error
    return latest as Cart
  }
  throw error
}

// --- B2B API functions ---

export const deleteCustomizationB2B = async (checkoutId: string, lineItemId: string): Promise<Cart> => {
  const normalizedLineItemId = normalizeId(lineItemId, 'lineItemId')
  try {
    const { data } = await storefrontCheckoutServicesHttpClient.delete(
      `/checkouts/${checkoutId}/cart/customization-items/${normalizedLineItemId}`,
    )
    return data as Cart
  } catch (error) {
    return await resolveDeleteError(error, checkoutId)
  }
}

export const editCustomizationB2B = async (
  checkoutId: string,
  lineItemId: string,
  payload: CustomizationEditPayload,
): Promise<Cart> => {
  const normalizedLineItemId = normalizeId(lineItemId, 'lineItemId')
  const { data } = await storefrontCheckoutServicesHttpClient.patch(
    `/checkouts/${checkoutId}/cart/customization-items/${normalizedLineItemId}`,
    payload,
  )
  return data as Cart
}

export const deleteGroupB2B = async (checkoutId: string, groupId: string): Promise<Cart> => {
  const normalizedGroupId = normalizeId(groupId, 'groupId')
  try {
    const { data } = await storefrontCheckoutServicesHttpClient.delete(
      `/checkouts/${checkoutId}/cart/grouped-line-items/${normalizedGroupId}`,
    )
    return data as Cart
  } catch (error) {
    return await resolveDeleteError(error, checkoutId)
  }
}
